// React
import { type ReactNode } from "react"

// UI primitives
import { CommandItem } from "@/components/ui/command"

// Hooks and utilities
import { cn } from "@/lib/utils"

export interface DropdownListItemProps {
  /** Must be unique per row -- cmdk keys the highlighted/selected item by this string. Use a stable id, never a display field like email or name. */
  value: string
  onSelect: () => void
  primary: ReactNode
  primaryTitle: string
  primarySuffix?: ReactNode
  secondary?: ReactNode
  secondaryTitle?: string
  isSelected?: boolean
  /** Extra terms cmdk matches the search against, beyond `value`. */
  keywords?: string[]
  /** Greys the row out; it can't be picked by pointer or keyboard. */
  disabled?: boolean
}

// 100ms ease-out matches the multi-select filter rows. Tailwind's bare
// `transition-colors` default (150ms, a curve that eases in at the start) reads
// as sluggish on hover, which users see dozens of times a session.
// Figma: grey regular text at rest; the active (pointer or keyboard) and the
// selected row both take dinnerplate-white-light with off-black text.
const ROW_CLASSES =
  "flex w-full cursor-pointer flex-col items-start gap-1 rounded-lg p-4 font-normal text-grey transition-colors duration-100 ease-out data-[selected=true]:bg-dinnerplate-white-light data-[selected=true]:text-off-black"
const SELECTED_ROW_CLASSES = "bg-dinnerplate-white-light text-off-black"

/**
 * Shared row primitive for combobox result lists. Owns the non-negotiable
 * truncation contract (block + w-full + min-w-0 + truncate + title) for the
 * primary line and an optional secondary line, so no consumer can regress it
 * by copying the classes by hand.
 */
export function DropdownListItem({
  value,
  onSelect,
  primary,
  primaryTitle,
  primarySuffix,
  secondary,
  secondaryTitle,
  isSelected = false,
  keywords,
  disabled = false,
}: DropdownListItemProps) {
  return (
    <CommandItem
      value={value}
      keywords={keywords}
      disabled={disabled}
      onSelect={onSelect}
      className={cn(ROW_CLASSES, isSelected && SELECTED_ROW_CLASSES)}
    >
      <div className="flex w-full min-w-0 items-center text-sm leading-normal">
        <span className="block min-w-0 truncate" title={primaryTitle}>
          <span>{primary}</span>
        </span>
        {primarySuffix != null && (
          <span className="ml-1 shrink-0">
            <span>{primarySuffix}</span>
          </span>
        )}
      </div>
      {secondary != null && (
        <span className="block w-full min-w-0 truncate text-xs leading-tight" title={secondaryTitle}>
          <span>{secondary}</span>
        </span>
      )}
    </CommandItem>
  )
}
